import Link from 'next/link';
import { memo, useCallback } from 'react';

import { DownloadIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { VisuallyHidden } from '@air/primitive-visually-hidden';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';

import { ToggleNestedItemsButton } from '~/_components/ToggleNestedItemsButton';
import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import { PublicSignUpButton } from '~/components/v2/components/board/PublicSignUpButton';
import { BoardBottombar } from '~/components/v2/components/board/shared/BoardBottombar';
import { AccountInformation } from '~/components/v2/components/shared/account/AccountInformation';
import { AirLogo } from '~/components/v2/components/shared/branding/AirLogo';
import { usePublicWhiteLabel } from '~/components/v2/hooks/useWhiteLabel';
import { getBrandedButtonStyle } from '~/components/v2/utils/filters/getContrastColor';
import { Routes } from '~/constants/routes';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useDownloadPublicShowcase } from '~/hooks/useDownloadPublicShowcase';
import { useHideAirLogo } from '~/hooks/useHideAirLogo';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useAccountContext } from '~/providers/AccountProvider';
import { canGenerateZip } from '~/utils/permissions/taskPermissions';

export const PublicShowcaseBottomBar = memo(() => {
  const { permissions } = usePublicPermissions();
  const { downloadPublicShowcase } = useDownloadPublicShowcase();
  const { isSearchActive } = useIsSearchActive();
  const { ui2026 } = useTemporaryFeatureFlags();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { data: account } = useAccountContext();
  const hasAccount = !!account;
  const { isWhiteLabel } = usePublicWhiteLabel();
  const { hideAirLogo } = useHideAirLogo();
  const { buttonBgColor } = usePublicCustomColors();
  const { objectId, objectType, presentationSettings } = useShortIdContext();
  const collectionId = objectType === 'collection' ? objectId : undefined;
  const hideDownloadAll = presentationSettings?.hideDownloadAll === true;
  const isExpandNestedDisabled = presentationSettings?.lockedControls?.expandNested === true;
  const isDownloadVisible = canGenerateZip(permissions) && !!collectionId && !hideDownloadAll;
  const isDownloadButtonVisible = !isSearchActive && isDownloadVisible;
  const isAirLogoVisible = !isWhiteLabel && !hideAirLogo;
  const isExpandNestedVisible =
    !ui2026 && !isSearchActive && !!collectionId && isAboveMediumScreen && !isExpandNestedDisabled;
  const isSignUpVisible = !hasAccount && !hideAirLogo;
  // Expand nested is desktop-only here when ui2026 is off (ui2026 moves it into view settings;
  // mobile uses ShowcaseHeaderViewControls). A logged-out
  // visitor with hide-Air-logo and no download control therefore gets an empty 48px bar on
  // mobile — and on desktop if expand nested is locked too (FOUND-880).
  const hasBottomBarContent =
    isDownloadButtonVisible || isAirLogoVisible || hasAccount || isExpandNestedVisible || isSignUpVisible;

  const onDownloadClick = useCallback(() => {
    if (collectionId) {
      downloadPublicShowcase({ collectionId, trackLocation: 'collection-stats-bar' });
    }
  }, [collectionId, downloadPublicShowcase]);

  if (!hasBottomBarContent) {
    return null;
  }

  return (
    <BoardBottombar data-testid="PUBLIC_SHOWCASE_BOTTOM_BAR">
      <div className="flex items-center gap-2">
        {isDownloadButtonVisible && (
          <Tooltip label="Download showcase">
            <IconButton
              adsIcon
              appearance="ghost"
              color="gray"
              data-testid="DOWNLOAD_COLLECTION_BUTTON"
              icon={DownloadIcon}
              label="Download showcase"
              onClick={onDownloadClick}
              style={buttonBgColor ? getBrandedButtonStyle(buttonBgColor) : undefined}
            />
          </Tooltip>
        )}
        {isAirLogoVisible && (
          <Link
            data-testid="PUBLIC_VIEWER_AIR_LOGO"
            href={Routes.marketing.root}
            rel="noopener noreferrer"
            target="_blank"
          >
            <VisuallyHidden>Visit Air's website</VisuallyHidden>
            {/*
             * `text-gray-12` rather than `text-black dark:text-white`: the `dark:` variant compiles to an
             * ancestor selector (`[data-theme="dark"] *`), which a nested light subtree cannot un-match.
             * ShowcaseEditor pins `data-theme="dark"` on the editor root and
             * ShowcaseEditorInlineCanvas re-declares `data-theme` for the canvas, so on a light link
             * the bar's background re-resolved light while the logo stayed white. Color tokens are CSS vars
             * re-declared at every `data-theme`, so they resolve against the nearest one instead.
             */}
            <AirLogo className="h-3 text-gray-12" />
          </Link>
        )}
        {hasAccount && (
          <>
            {/* We `first:hidden` to hide the separator when the previous div doesn't exist */}
            <div className="h-4 w-px bg-gray-8 first:hidden" />
            <AccountInformation />
          </>
        )}
        {/* TODO: Add collection stats here */}
      </div>
      <div className="flex items-center gap-2">
        {isExpandNestedVisible && <ToggleNestedItemsButton />}
        {isSignUpVisible && <PublicSignUpButton />}
      </div>
    </BoardBottombar>
  );
});

PublicShowcaseBottomBar.displayName = 'PublicShowcaseBottomBar';
